import type { AgentPromptRequest } from '@robinpowered/dashboard-apps-common';

type AgentPromptSubscriber = (request: AgentPromptRequest) => void;

/** Reader view, handed to the agent parcel as a customProp. */
export type AgentPromptBridgeRef = {
  /**
   * Subscribe to prompts the host has asked the assistant to run. Subscribing
   * delivers a prompt that was waiting for the parcel to mount, if there is
   * one and it is still fresh. Returns an unsubscribe function.
   */
  subscribe: (cb: AgentPromptSubscriber) => () => void;
};

/** Writer view, kept by the shell. */
export type AgentPromptBridge = AgentPromptBridgeRef & {
  /** Deliver now if the parcel is listening, otherwise hold for its mount. */
  send: (request: AgentPromptRequest) => void;
  /**
   * Drop every subscriber and anything held. The shell calls this when it
   * tears the parcel down, so subscriber lifetime is bounded by the parcel
   * lifecycle it owns rather than by cleanup code in another repo's bundle.
   */
  reset: () => void;
};

/**
 * How long a prompt may wait for a parcel that never arrives.
 *
 * The assistant can fail to open after a prompt is sent — the composer holds
 * the right-hand slot and the user backs out of its discard dialog, so the
 * agent param is dropped and nothing ever mounts. Without an expiry the prompt
 * would sit here until the user opened the assistant by some other route,
 * whereupon a question from an hour ago would land in a fresh conversation.
 */
const PENDING_PROMPT_TTL_MS = 60_000;

/**
 * A prompt sent from a cold sider has to survive the wait.
 *
 * The agent parcel mounts lazily on first open and takes a moment to fetch its
 * bundle, so a dashboard CTA can fire before anything is listening. Dropping
 * the request there would make the button look broken exactly once per
 * session — the first time anyone uses it. So `send` holds it, and the parcel
 * collects it when it subscribes.
 *
 * One slot, latest wins, rather than a queue. A prompt is an action, delivered
 * once (see the channel contract in common's `types/agentPromptChannel.ts`).
 * Someone clicking the CTA twice while a 2 MB bundle loads means one question
 * asked impatiently, not two questions — and every request carries its own id,
 * so the receiver's dedupe cannot collapse them. Queueing them would put the
 * same question into the chat twice.
 */
export function createAgentPromptBridge(): AgentPromptBridge {
  const subscribers = new Set<AgentPromptSubscriber>();
  let pending: { request: AgentPromptRequest; sentAt: number } | null = null;

  return {
    send(request) {
      if (subscribers.size > 0) {
        // Snapshot so a subscriber that (un)subscribes during dispatch doesn't
        // mutate the set we're iterating.
        [...subscribers].forEach((cb) => cb(request));
        return;
      }
      pending = { request, sentAt: Date.now() };
    },
    reset() {
      // A subscriber that outlives its parcel is worse than none: send() would
      // see a non-empty set, dispatch into the dead callback and never hold
      // for the live parcel, so the assistant would go quiet for the rest of
      // the session with nothing logged. A held prompt goes too — it was aimed
      // at a parcel that is going away.
      subscribers.clear();
      pending = null;
    },
    subscribe(cb) {
      subscribers.add(cb);
      // Claim the slot before dispatching, so a second subscriber cannot run a
      // prompt the first one already took.
      const waiting = pending;
      pending = null;
      if (waiting && Date.now() - waiting.sentAt <= PENDING_PROMPT_TTL_MS) {
        cb(waiting.request);
      }
      return () => {
        subscribers.delete(cb);
      };
    },
  };
}
